import { FacebookSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import { taggedPhotosUrl } from './fbUrls'
import { FACEBOOK_PHOTO_STAGE_TIMEOUT } from '../../consts/stageMachineConsts'
import * as state from '../../state'
import { FBRecommendationKey, PLATFORM, StageType } from '../../utils/enums'
import { _debug } from '../../utils/logging'
import { logError } from '../../utils/loggingUtils'
import { StageDef } from '../automationMachineInterfaces'
import {
  getStoredMachineOverlayStatus,
  setStoredMachineOverlayStatus,
} from '../automationStageMachine'
import { throwIfGlobalInterruptRequested } from '../globalInterrupt'
import * as promises from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveAttribute,
  getCaseInsensitiveString,
  getCaseInsensitiveXPathTextSelector,
} from '../interruptibleXpath'
import {
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import {
  getElement,
  getOptionalElement,
  getInitialValueBySettingKey,
} from '../utils'
import { getNameAccountMenuMethod } from './facebookGatherAccountUsername'

/**
 *  Setting property functions. Will not really be used for this recommendation
 *  due to custom type.
 */

function getIsPhotoTagsFulfilled(initialState): boolean {
  const backwardsCompatibleInitialState = getInitialValueBySettingKey(
    initialState,
    FacebookSettingKey.PHOTO_UNTAGGING,
  )

  if (
    backwardsCompatibleInitialState === null ||
    backwardsCompatibleInitialState === undefined
  ) {
    return false
  }

  let totalPhotoCount = 0

  if (backwardsCompatibleInitialState.batches) {
    Object.keys(backwardsCompatibleInitialState.batches).forEach((batchId) => {
      totalPhotoCount += backwardsCompatibleInitialState.batches[batchId].length
    })
  }

  return totalPhotoCount === 0
}

const getCurrentValueFn = () => () => null
const getIsFulfilledFn = () => getIsPhotoTagsFulfilled
const getCantUndoFn = () => () => true
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const BATCH_SIZE = 100
const MAX_NEW_PHOTOS_PER_GATHER = 500

// Utilities for interacting with Facebook site

/**
 * Collects all elements on Facebook's photo grid.
 *
 * The <a> elements wrap an <img> element
 * - <a> href has the photo id (fbid)
 * - <img> src has the thumbnail url
 */
function getAllPhotoAnchorElements(): HTMLAnchorElement[] {
  return evaluateXpath<HTMLAnchorElement>(
    document,
    '//a[' +
      '  count(./descendant::img) > 0 and ' +
      '  contains(@href, "photo.php") and ' +
      '  contains(@href, "fbid")' +
      ']',
  )
}

function getFacebookPhotoIdFromPhotoAnchorElement(
  element: HTMLAnchorElement,
): string {
  const href = element.href
  const match = href.match(/fbid=(\d+)/)?.[1]
  if (!match) {
    throw new Error(`Could not find facebook photo id in href: ${href}`)
  }

  return match
}

function getFacebookThumbnailUrl(element: HTMLAnchorElement): string {
  const imgElement = evaluateXpath<HTMLImageElement>(
    element,
    './descendant::img',
  )[0]
  return imgElement.src
}

// Additional utilities, working with / writing to initial values

type PhotoInfo = {
  photoId: string
  thumbnailUrl: string
}

interface InitialValues {
  batches?: Record<string, PhotoInfo[]>
  isPhotoUntagged?: Record<string, boolean>
}

function getChunks<T>(array: T[], size: number): T[][] {
  const chunks = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }

  return chunks
}

function getExistingPhotoIdsFromInitialValues(
  initialValues: InitialValues | undefined,
): Set<string> {
  const batches = initialValues?.batches

  if (!batches) {
    return new Set<string>()
  }

  const existingPhotoIds = new Set<string>()
  Object.values(batches).forEach((batch) =>
    batch.forEach((photo) => existingPhotoIds.add(photo.photoId)),
  )

  return existingPhotoIds
}

function getVisiblePhotosElements(): PhotoInfo[] {
  const elements = getAllPhotoAnchorElements()
  const photoInfo = elements.map((element) => ({
    photoId: getFacebookPhotoIdFromPhotoAnchorElement(element),
    thumbnailUrl: getFacebookThumbnailUrl(element),
  }))

  return photoInfo
}

function gatherPhotoData(
  batchSize: number,
  maxNumPhotos: number,
  initialValues: InitialValues | undefined,
  photoData: Map<string, string>,
): Record<number, any[]> {
  const excludedPhotoIds = getExistingPhotoIdsFromInitialValues(initialValues)
  excludedPhotoIds.forEach((id) => photoData.delete(id))

  const photoInfoArr = Array.from(photoData, ([photoId, thumbnailUrl]) => ({
    photoId,
    thumbnailUrl,
  })).slice(0, maxNumPhotos)

  // Append new photos to existing batches object, using auto-incrementing
  // integer keys
  const batches = initialValues?.batches || {}
  const batchKey = Object.keys(batches).length
  Array.from(getChunks(photoInfoArr, batchSize).entries()).forEach(
    ([index, data]) => {
      batches[batchKey + index] = data
    },
  )

  return batches
}

async function updateStageStatusWithCurrentPhotoIxNew(
  currentPhotoIx: number,
  nTotalPhotos: number,
): Promise<void> {
  const currentStatus = await getStoredMachineOverlayStatus(PLATFORM.FACEBOOK)

  await setStoredMachineOverlayStatus(PLATFORM.FACEBOOK, {
    ...currentStatus,
    currentPhotoIx,
    nTotalPhotos,
  })
}

enum StageKey {
  GATHER_TAGGED_PHOTOS = 'GATHER_TAGGED_PHOTOS',
  UPDATE_TAGGED_PHOTOS = 'UPDATE_TAGGED_PHOTOS',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_TAGGED_PHOTOS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    url: taggedPhotosUrl,
    urlPattern: 'facebook.com.+photos_of',
    action: async (machineContext, userContext) => {
      const {
        platform,
        userId,
        initialValues: storedInitialValues,
        updateStoredInitialValues,
      } = userContext

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        FacebookSettingKey.PHOTO_UNTAGGING,
      )

      const updatedInitialValues = {
        ...initialValues,
      }

      const photoData = new Map<string, string>()

      let numTryNoChange = 0
      let prevNumPhotoIds = 0
      let prevScrollHeight = window.scrollY

      while (numTryNoChange < 5 && photoData.size < MAX_NEW_PHOTOS_PER_GATHER) {
        window.scrollBy(0, 2000)
        await promises.wait(1000)

        const newPhotos = getVisiblePhotosElements()
        newPhotos.forEach((photoInfo) => {
          photoData.set(photoInfo.photoId, photoInfo.thumbnailUrl)
        })

        if (photoData.size > prevNumPhotoIds) {
          prevNumPhotoIds = photoData.size
        } else if (photoData.size === prevNumPhotoIds) {
          numTryNoChange += 1
        }

        prevScrollHeight = window.scrollY
        throwIfGlobalInterruptRequested()
      }

      const batches = gatherPhotoData(
        BATCH_SIZE,
        MAX_NEW_PHOTOS_PER_GATHER,
        initialValues,
        photoData,
      )

      window.scrollBy(0, 1000)
      await promises.wait(500)

      updatedInitialValues.batches = batches
      updatedInitialValues.hasLoadedAllPhotos =
        window.scrollY === prevScrollHeight

      await updateStoredInitialValues(
        {
          [FacebookSettingKey.PHOTO_UNTAGGING]: updatedInitialValues,
        },
        FBRecommendationKey.FB_PHOTO_TAGS_V2,
      )

      if (photoData.size > 0) {
        await state.setRecommendationStatus(
          platform,
          userId,
          FBRecommendationKey.FB_PHOTO_TAGS_V2,
          { undoUpdated: true },
        )
      }
    },
  },
]

/**
 * Help us find (and remove) tags on the Photo UX.
 * For use when the "remove tags" edit menu icon is not available.
 */

/**
 * Look for a placeholder like "Comment as Blobby McParty"
 */
export async function getNameCommentAsMethod(): Promise<string> {
  const inputElement = await getElement<HTMLElement>(
    document,
    '//*[contains(@aria-label, "Comment as")]',
  )

  const label = inputElement.getAttribute('aria-label')

  const regexMatch = /comment as (.+)/i.exec(label)

  if (!regexMatch || !regexMatch[1]) {
    throw new Error(
      'getNameCommentAsMethod: Could not find regex match on element ' +
        `(found aria-label: ${label})`,
    )
  }

  return regexMatch[1]
}

export async function getAndRemoveTagNewMethod(isDry: boolean): Promise<void> {
  // First, get the name (e.g., Blobby McParty)
  let name: string
  try {
    name = await getNameAccountMenuMethod()
    remoteLog.debug(
      'getAndRemoveTagNewMethod: succeeded using account menu method',
    )
  } catch (_error) {
    name = await getNameCommentAsMethod()
    remoteLog.debug(
      'getAndRemoveTagNewMethod: succeeded using comment as method',
    )
  }

  const showTagsButton = await getElement<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveXPathTextSelector('aria-label', 'tag photo')}]`,
  )
  showTagsButton.click()

  // Next, get the tag & click it; this should reveal the delete tag element
  const tagElement = await getElement<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveAttribute('aria-label')} = "tag for ${name.toLowerCase()}"]`,
  )
  await promises.wait(500)
  tagElement.click()

  // Find the delete tag element, wait a sec (to seem less robotic) and click
  const deleteTagElement = await getElement<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveAttribute('aria-label')} = "delete tag for ${name.toLowerCase()}"]`,
  )
  promises.wait(500)

  if (!isDry) {
    deleteTagElement.click()
  }
}

export async function getAndRemoveTagOldMethod(
  isDry: boolean,
  profileUrl: string,
): Promise<void> {
  const showTagsButton = await getElement<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveXPathTextSelector('aria-label', 'tag photo')}]`,
  )
  showTagsButton.click()

  await promises.wait(500)
  await getElement<HTMLElement>(document, '//a[@role="menuitem"]')

  // Find tags of user
  const tags = evaluateXpath<HTMLAnchorElement>(
    document,
    '//a[@role="menuitem"]',
  )
  const userTags = tags.filter((tag) => tag.href.startsWith(profileUrl))
  if (userTags.length === 0) {
    throw new Error('Could not find tag of user')
  } else if (userTags.length > 1) {
    throw new Error('Found multiple tags matching user')
  }

  if (!isDry) {
    const userTag = userTags[0]
    const removeTag = await getElement<HTMLElement>(
      userTag,
      `descendant::*[@role="button" and ${getCaseInsensitiveXPathTextSelector(
        'aria-label',
        'delete tag',
      )}]`,
    )
    removeTag.click()
  }
}

export async function untagPhoto(
  profileUrl: string,
  photoId: string,
  isDry: boolean,
) {
  const photoAnchor = await getElement<HTMLAnchorElement>(
    document,
    `//a[contains(@href, "?fbid=${photoId}")]`,
  )

  const editPhotoButton = await getElement<HTMLDivElement>(
    photoAnchor,
    `./descendant::div[${getCaseInsensitiveString('@aria-label')}="more options for this photo" and @role="button"]`,
  )
  editPhotoButton.click()

  const removeTagButton = await getOptionalElement<HTMLElement>(
    document,
    '//*[@role="menuitem" and descendant::*[contains(text(), "Remove Tag")]]',
  )

  if (removeTagButton) {
    removeTagButton.click()

    await promises.wait(500)
    if (isDry) {
      _debug('Dry run: not removing tag', 'facebook untagging automation')
      const closeButton = await getElement<HTMLDivElement>(
        document,
        `//div[${getCaseInsensitiveString('@aria-label')}="cancel" and @role="button"]`,
      )

      closeButton.click()
    } else {
      const okButton = await getElement<HTMLDivElement>(
        document,
        `//div[${getCaseInsensitiveString('@aria-label')}="ok" and @role="button"]`,
      )

      okButton.click()
    }
  } else {
    // No remove tag in menu (photo belongs to user). Must remove tag from photo link.
    const waitForPhotoToLoad = promises.getUrlChangePromise()
    photoAnchor.click()
    await waitForPhotoToLoad

    let success = false
    try {
      await getAndRemoveTagOldMethod(isDry, profileUrl)
      success = true
      remoteLog.debug('untagPhoto: succeeded using old untagging ux')
    } catch {
      // pass
    }

    if (!success) {
      await getAndRemoveTagNewMethod(isDry)
      remoteLog.debug('untagPhoto: succeeded using new untagging ux')
    }

    const closePhotoButton = await getElement<HTMLElement>(
      document,
      `//div[@role="banner"]/descendant::*[${getCaseInsensitiveXPathTextSelector(
        'aria-label',
        'close',
      )} and @role="button"]`,
    )
    closePhotoButton.click()
  }

  await promises.wait(500)
}

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_TAGGED_PHOTOS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    url: taggedPhotosUrl,
    urlPattern: 'facebook.com.+photos_of',
    action: async (machineContext, userContext, additionalMachineData) => {
      // Search for the selected photos starting at top of page
      window.scrollTo(0, 0)

      // Matches both old (?sk=photos_of) and new (/photos_of) URL formats
      const profileUrlPattern = new RegExp(
        '(https://www.facebook.com/' +
          '(?:profile\\.php\\?id=\\d+|[^/?&]+))' +
          '(?:/photos_of|/\\?sk=photos_of|\\?sk=photos_of|&sk=photos_of)',
      )
      const profileUrl = profileUrlPattern.exec(window.location.href)?.[1]

      if (!profileUrl) {
        throw new Error('Could not find profile url')
      }

      const {
        initialValues: storedInitialValues,
        isDry,
        updateStoredInitialValues,
      } = userContext

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        FacebookSettingKey.PHOTO_UNTAGGING,
      )

      const prevIsPhotoUntaggedMap = initialValues?.isPhotoUntagged || {}

      const { inputData, resumeData } = additionalMachineData

      if (!inputData || !inputData.selectedPhotoIds) {
        throw new Error(
          'Automation must be provided the ids of the photos to untag.',
        )
      }

      const selectedPhotoIds = inputData.selectedPhotoIds
      if (!selectedPhotoIds) {
        logError(
          new Error('User must provide selectedPhotoIds, but none found'),
          'fb-photo-tags',
        )
        return null
      }

      const previouslyProcessedPhotoIds = new Set(
        resumeData.data?.processedPhotoIds || [],
      )

      const photoIdsToUntag = selectedPhotoIds.filter(
        (photoId) =>
          !prevIsPhotoUntaggedMap[photoId] &&
          !previouslyProcessedPhotoIds.has(photoId),
      )

      let allPhotosFound = false
      while (!allPhotosFound) {
        window.scrollBy(0, 500)
        await promises.wait(500)

        const visiblePhotoIds = new Set(
          getAllPhotoAnchorElements().map((element) =>
            getFacebookPhotoIdFromPhotoAnchorElement(element),
          ),
        )
        allPhotosFound = photoIdsToUntag.every((photoId) =>
          visiblePhotoIds.has(photoId),
        )

        throwIfGlobalInterruptRequested()
      }

      const currentProcessedPhotoIds = []
      for (const photoId of photoIdsToUntag) {
        updateStageStatusWithCurrentPhotoIxNew(
          previouslyProcessedPhotoIds.size +
            currentProcessedPhotoIds.length +
            1,
          selectedPhotoIds.length,
        )

        await promises.wait(500)
        await untagPhoto(profileUrl, photoId, isDry)

        await userContext.incrementTotalCount(
          FBRecommendationKey.FB_PHOTO_TAGS_V2,
        )

        // Update untagged data if not dry run
        if (!isDry) {
          await updateStoredInitialValues(
            {
              [FacebookSettingKey.PHOTO_UNTAGGING]: {
                ...initialValues,
                isPhotoUntagged: {
                  ...prevIsPhotoUntaggedMap,
                  [photoId]: true,
                },
              },
            },
            FBRecommendationKey.FB_PHOTO_TAGS_V2,
          )

          prevIsPhotoUntaggedMap[photoId] = true
        }

        currentProcessedPhotoIds.push(photoId)

        await userContext.updateStatisticsLastCount(
          FBRecommendationKey.FB_PHOTO_TAGS_V2,
          previouslyProcessedPhotoIds.size + currentProcessedPhotoIds.length,
        )

        await resumeData.set({
          processedPhotoIds: [
            ...previouslyProcessedPhotoIds,
            ...currentProcessedPhotoIds,
          ],
        })
      }

      return null
    },
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
